import { useMemo, useState } from 'react'
import { Inbox } from 'lucide-react'
import styles from './BookingFlow.module.css'
import BlankState from '../BlankState/BlankState'
import Meter, { SegmentedMeter } from '../Meter/Meter'
import { fmtInt } from '../../../utils/moneyFormat'

// Fixed reading order — the pipeline is scanned the way a booking moves
// through it, not sorted by size, so the panel never reshuffles on refresh.
// Confirmed and Pending are always shown; the rest disappear when empty.
const STATUSES = [
  { key: 'confirmed', label: 'Confirmed', tone: 'pos', pinned: true },
  { key: 'pending', label: 'Pending', tone: 'warn', pinned: true },
  { key: 'completed', label: 'Completed', tone: 'accent', pinned: false },
  { key: 'cancelled', label: 'Cancelled', tone: 'mute', pinned: false },
]

// Stored product types are single words; only CarRental needs splitting.
const PRODUCT_LABELS = {
  CarRental: 'Car Rental',
}

// Each product line keeps the same colour wherever it appears, so the mix is
// readable without tracing labels.
const PRODUCT_TONES = {
  Flight: 'accent',
  Hotel: 'brand',
  Transfer: 'teal',
  Insurance: 'violet',
  CarRental: 'pos',
  Package: 'warn',
  Visa: 'mute',
}

const pctLabel = (share, count) => {
  if (!count) return '0%'
  if (share > 0 && share < 1) return '<1%'
  return `${Math.round(share)}%`
}

// Live booking status distribution (data from /dashboard/overview →
// bookingFlow). One stacked bar carries the shape, the legend carries the
// numbers, and hovering either one quiets everything else.
const BookingFlow = ({ data, periodLabel, loading }) => {
  const [active, setActive] = useState(null)

  const { total, segments, legend } = useMemo(() => {
    const rows = STATUSES.map((s) => ({
      ...s,
      count: Math.max(0, Number(data?.[s.key]) || 0),
    }))
    const summed = rows.reduce((acc, r) => acc + r.count, 0)
    // Trust whichever denominator is larger: a reported total that lags the
    // status counts would push the bar past 100%.
    const base = Math.max(summed, Math.max(0, Number(data?.total) || 0))
    const withShare = rows.map((r) => ({
      ...r,
      share: base ? (r.count / base) * 100 : 0,
    }))
    return {
      total: base,
      segments: withShare
        .filter((r) => r.count > 0)
        .map((r) => ({
          key: r.key,
          tone: r.tone,
          pct: r.share,
          title: `${r.label} · ${fmtInt(r.count)} (${pctLabel(r.share, r.count)})`,
        })),
      legend: withShare.filter((r) => r.pinned || r.count > 0),
    }
  }, [data])

  const newToday = Number(data?.newToday) || 0
  const allTimeTotal = Number(data?.allTimeTotal) || 0

  // What those bookings are made of. Bars are scaled against the biggest
  // component rather than the total, so the smaller lines stay readable.
  const mix = useMemo(() => {
    const rows = (data?.productMix || [])
      .map((r) => ({
        type: r.type,
        label: PRODUCT_LABELS[r.type] || r.type,
        tone: PRODUCT_TONES[r.type] || 'accent',
        bookings: Number(r.bookings) || 0,
      }))
      .filter((r) => r.bookings > 0)
    const top = rows.reduce((m, r) => Math.max(m, r.bookings), 0)
    return rows.map((r) => ({ ...r, share: top ? (r.bookings / top) * 100 : 0 }))
  }, [data])

  return (
    <div className={`dshPanel ${styles.root}`}>
      <div className="dshHead">
        <div className={styles.heading}>
          <h3 className="dshTitle">Booking Pipeline</h3>
          {/* Bookings *created* in the period, shown at their status today —
              so the subtitle has to name the period, not claim to be live. */}
          <p className="dshSub dshTrunc">
            Created in {periodLabel || 'this period'}
          </p>
        </div>
        {!loading && total > 0 && (
          <span className={`dshPill dshPillMute dshNum ${styles.totalPill}`}>
            {fmtInt(total)} total
          </span>
        )}
      </div>

      <div className={`dshBody ${styles.body}`}>
        {loading && (
          <>
            <span className={`dshSkel ${styles.barSkel}`} />
            <div className={styles.legend}>
              {STATUSES.map((s) => (
                <div className="dshRow" key={s.key}>
                  <span className={`dshSkel ${styles.labelSkel}`} />
                  <span className={`dshSkel ${styles.valueSkel}`} />
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && total === 0 && (
          <BlankState
            icon={Inbox}
            title={`No bookings in ${periodLabel || 'this period'}`}
            text={
              allTimeTotal > 0
                ? `Nothing was created in this period. ${fmtInt(
                    allTimeTotal
                  )} bookings exist across all time — widen the period to see them.`
                : 'Confirmed, pending and cancelled bookings will appear here as soon as the first one lands.'
            }
            className={styles.blank}
          />
        )}

        {!loading && total > 0 && (
          <>
            <SegmentedMeter
              segments={segments}
              size="lg"
              className={styles.track}
              activeKey={active}
              onSegmentEnter={setActive}
            />

            <div className={styles.legend}>
              {legend.map((s) => (
                <div
                  className={`dshRow ${styles.legendRow} ${
                    active && active !== s.key ? styles.legendDim : ''
                  }`}
                  key={s.key}
                  onMouseEnter={() => setActive(s.key)}
                  onMouseLeave={() => setActive(null)}
                >
                  <span className={styles.legendLabel}>
                    <span className={`${styles.dot} ${styles[`dot${s.tone}`]}`} />
                    <span className="dshTrunc">{s.label}</span>
                  </span>
                  <span className={styles.legendValue}>
                    <span className={`dshNum ${styles.count}`}>{fmtInt(s.count)}</span>
                    <span className={`dshNum ${styles.share}`}>
                      {pctLabel(s.share, s.count)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && mix.length > 0 && (
          <div className={styles.mix}>
            <span className={`dshEyebrow ${styles.mixLabel}`}>Product mix</span>
            {mix.map((m, i) => (
              <div key={m.type} className={styles.mixRow}>
                <span className={`dshTrunc ${styles.mixType}`}>{m.label}</span>
                <Meter pct={m.share} tone={m.tone} size="sm" delay={i * 70} />
                <span className={`dshNum ${styles.mixCount}`}>{fmtInt(m.bookings)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Two figures the period must not move: what landed today, and the
            size of the whole book. They anchor the filtered numbers above. */}
        <div className={styles.footer}>
          <div className={styles.foot}>
            <span className="dshEyebrow">New today</span>
            {loading ? (
              <span className={`dshSkel ${styles.footSkel}`} />
            ) : (
              <span className={`dshNum ${styles.footValue}`}>{fmtInt(newToday)}</span>
            )}
          </div>
          <div className={`${styles.foot} ${styles.footEnd}`}>
            <span className="dshEyebrow">All bookings</span>
            {loading ? (
              <span className={`dshSkel ${styles.footSkel}`} />
            ) : (
              <span className={`dshNum ${styles.footValue}`}>{fmtInt(allTimeTotal)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BookingFlow
